/**
 * A session's thread on the new sync stack — the transcript (Thread, reading the
 * clean projectSessionTranscript projection) plus a composer that appends user
 * messages (appendMessage).
 *
 * The transcript is a full rewrite (Thread → Message → Markdown / ToolCallBlock),
 * NOT the legacy MessageList (which renders the fat agentPanelStore model).
 * Artifacts + canvas-preview cards are deferred to the canvas slice; the composer
 * stays minimal here (composer polish is its own slice).
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  decodeLogDoc,
  heads,
  pageSeedOf,
  projectSessionBranch,
} from '@mod/core/log'
import {
  type AttachmentRef,
  attachFile,
  commentListView,
  createSkill,
  type InteractionAnswerValue,
  type MessageView,
  parseQuestionBody,
  projectAuthor,
  projectCode,
  projectComments,
  projectDocuments,
  projectSessionActiveRun,
  projectSessionCode,
  projectSessionHoldings,
  projectSessionPendingInteractions,
  projectSessions,
  projectSources,
  registryStatus,
  type SessionDoc,
  usesPrivateSessionCatalog,
} from '@mod/core/model'
import { answerInteraction, cancelRun, dismissInteraction } from '@mod/core/run'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { fetchVcsFromServer } from '../../sdk/vcs'
import { sessionChangesContribution } from '../../services/apps/sessionChangesContribution'
import { useCommentMap } from '../../services/comments/useCommentMap'
import type { ProjectChoice } from '../../services/sessions/projectChoices'
import { useSelection } from '../../services/sessions/selection'
import { sessionFace } from '../../services/sessions/sessionFace'
import { desktopInviteFor } from '../../services/sharing/desktopShare'
import { useDocumentApprovals } from '../../services/sharing/documentApprovals'
import { useGuestSessionDocuments } from '../../services/sharing/guestDocuments'
import { useGuestReplies } from '../../services/sharing/guestReplies'
import { useSharedSessionInviter } from '../../services/sharing/useSharedSessionInviter'
import { pickableSkills } from '../../services/skills/skillCatalog'
import { useSkills } from '../../services/skills/useSkills'
import type { MessageViewContext } from '../../services/view/viewManifest'
import { useTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { useDocument } from '../../services/world/repo/useDocument'
import { useComposerDevices } from '../../services/world/useComposerDevices'
import { useInstalledApps } from '../../services/world/useInstalledApps'
import { readWorldPointer } from '../../services/world/world'
import { InstalledSlot } from '../../shell/slots/InstalledSlot'
import { fillSlotAll } from '../../shell/slots/slots'
import { browserProcessImage, fileToSource } from './attachments'
import { makeBranchFrontier } from './branchFrontier'
import { openBranchReview } from './branchReview'
import type { CommentLookup } from './CommentInThread'
import { Composer, preferredComposerModel } from './Composer'
import { DocumentApprovals } from './DocumentApproval'
import { EditUndo } from './EditUndo'
import { GuestReplies } from './GuestReplyCard'
import { SessionHoldingsBar } from './HoldingsBar'
import { ProjectAccessApproval } from './ProjectAccessApproval'
import { QuestionForm } from './QuestionForm'
import { isReading } from './quietTurns'
import { completedTurnMessages } from './runFileChanges'
import { SessionChangesPanel } from './SessionChangesPanel'
import { SessionProjects } from './SessionProjects'
import { SessionShare } from './SessionShare'
import { SessionWhere } from './SessionWhere'
import {
  mergeSessionChanges,
  projectSessionChanges,
  type SessionChanges,
} from './sessionChanges'
import { sendToSession } from './startSession'
import { useStopTrailing } from './stopVerdict'
import { textAnswer } from './stripQuestion'
import { subthreadState } from './subthreadState'
import { Thread } from './Thread'
import type { CardTarget } from './threadCards'
import { useNotArrived } from './useNotArrived'
import { useSessionTranscript } from './useSessionTranscript'
import { useSessionVoice } from './useSessionVoice'

export function ThreadView({
  sessionId,
  projectId,
  onOpenDocument,
  onOpenSession,
  projects,
  onSelectProject,
  headerSlot,
  messageView,
  captureMessageView,
}: {
  sessionId: string
  /** Put a document on the canvas. Threaded from the shell, which owns the
   *  layout; without it the composer cannot offer to write a skill, because
   *  writing one you cannot then edit is half a feature. */
  onOpenDocument?: (documentId: string) => void
  /** Open another session in this block. The door into a sub-session an agent
   *  started, and once the list stops listing those, the ONLY door — so a
   *  context that cards a sub-session without threading this leaves it
   *  unreachable rather than merely unclickable. */
  onOpenSession?: (sessionId: string) => void
  /** The project, so the session-task strip can ask whether Tasks is
   *  installed. Absent in a context that has not threaded it through yet, in
   *  which case the strip simply does not render. */
  projectId?: string
  /** Where this session could work instead (RFC 0290 S6). Passed rather than
   *  read here, like `skills` and `devices`: this view renders in tests and in
   *  slots with no World in context. */
  projects?: ProjectChoice[]
  /** Move the session to another project. The caller owns the write and the
   *  re-pointing of the pane, because it is the one that knows which pane this
   *  is. Absent means the switcher is not drawn. */
  onSelectProject?: (projectId: string) => void
  /**
   * Where the session-header widgets draw (RFC 0278).
   *
   * A node in the PANE's header, published by the pane, portalled into from
   * here — the same move the composer makes to reach the collapsed panel. The
   * widgets need the transcript, and the transcript is opened here; the header
   * is a row the pane owns. One of the two has to travel, and a DOM node is
   * cheaper to move than the largest read this view makes.
   *
   * Null draws in place, above the thread. That is what a host with nowhere to
   * send them passes, and what every render before the anchor mounts sees.
   */
  headerSlot?: HTMLElement | null
  /** RFC 0304 3d: the window captured by the shell at send time. A thread
   *  composer can sit beside the work just as the compact strip can; omitting
   *  this here made the two composers disagree about what “this” meant. */
  messageView?: MessageView | null
  /** Desktop send-time pixels. Absent in browsers and isolated tests. */
  captureMessageView?: () => Promise<MessageViewContext>
}) {
  const boot = useTabRepoBoot()
  // RFC 0317: held, so the tab repo lets the log go when the pane closes.
  const [sessionDoc] = useDocument(sessionId as AnyDocumentId)

  // Memory-bounded transcript: inline tail instantly, then a WINDOW of recent
  // pages (throttled/guarded/never-shrink, evicted on unmount). A huge session no
  // longer loads all its pages into wasm at once. "Load earlier" widens the window.
  const { transcript, liveTranscript, hasMore, loadMore } =
    useSessionTranscript(boot.repo, sessionId, sessionDoc)

  // RFC 0345: a session shared with this person that does not open yet is
  // waiting on its owner's device to let them in (it settles the invite every
  // few seconds while open). Say so, rather than showing an empty thread and a
  // send that spins until the document is unavailable.
  const guestOf = useSharedSessionInviter(sessionId)
  const waitingForAccess = guestOf !== undefined && !sessionDoc
  // A session nobody let this person into looks, from here, exactly like one
  // still syncing: the relay answers both with nothing. After a grace period,
  // say both, and take the composer away, since a send has no log to land on.
  // The thread still opens the moment the document arrives.
  const unreachable = useNotArrived(!sessionDoc && guestOf === undefined)

  // The agent's actual state, read off the run rather than guessed from the
  // transcript's shape. `busy` draws Stop beside Send, and since RFC 0302 S1
  // that is all it does: the send itself is never gated. `waiting` distinguishes
  // "queued with nobody draining it" from "a worker is working", which is the
  // difference between a slow agent and no agent at all.
  const activeRun = useMemo(
    () => projectSessionActiveRun(sessionDoc),
    [sessionDoc],
  )
  const busy = activeRun !== null
  const waiting = activeRun?.claimed === false
  // The branch frontier THIS render shows: what a post typed now would descend
  // from. A render showing several heads sends against all of them, which is a
  // join.
  const visibleHeads = useMemo(
    () =>
      sessionDoc
        ? heads(decodeLogDoc(sessionDoc as Record<string, unknown>))
        : [],
    [sessionDoc],
  )
  // Through a REF, so `send` keeps its identity while the frontier moves under
  // it. A callback rebuilt on every peer message re-renders the box being typed
  // in, which is the cost the pin below is trying to avoid paying twice.
  const visibleHeadsRef = useRef(visibleHeads)
  visibleHeadsRef.current = visibleHeads
  /**
   * RFC 0302 S6. Pinned when the draft opens, so another person's message
   * landing while you type does not become the parent of what you are writing.
   * Per session: moving the pane to another thread must not carry a frontier
   * from a log this one has never seen.
   */
  const frontier = useMemo(() => makeBranchFrontier(), [])
  const onDraftChange = useCallback(
    (text: string) => frontier.draftChanged(text, visibleHeadsRef.current),
    [frontier],
  )
  /**
   * Where this session was branched from, when it is a subthread (RFC 0302 S4).
   *
   * Off its OWN log — the child records it, so reading it costs nothing beyond
   * the document this view has already opened, and a pane on a subthread never
   * has to hydrate the parent to know it has one.
   */
  const origin = useMemo(
    () =>
      sessionDoc
        ? projectSessionBranch(
            decodeLogDoc(sessionDoc as Record<string, unknown>),
          )
        : null,
    [sessionDoc],
  )
  // RFC 0301 §3f. Read off the transcript the thread hides, so the status and
  // the empty thread cannot disagree about whether the agent has spoken.
  const reading = useMemo(
    () => isReading(transcript, activeRun?.claimed === true),
    [transcript, activeRun],
  )

  const stop = useCallback(async () => {
    await cancelRun(boot.repo, sessionId)
  }, [boot.repo, sessionId])

  // RFC 0297 4e. Held in a ref because `send` must not be rebuilt every time
  // somebody drags across a paragraph: the composer memoises on it, and a new
  // callback per selectionchange would re-render the box being typed in.
  const { selection } = useSelection()
  const selectionRef = useRef(selection)
  selectionRef.current = selection

  // Placement (RFC 0261 slice 3): the live devices this session can run on, the
  // one it is currently placed on (its selected_tool_worker), and a way to
  // change it (append a worker_migration).
  const devices = useComposerDevices()

  // The question a blocked turn is waiting on, if any. Only the oldest is
  // shown: answering it is what lets the turn continue, and stacking several
  // forms would imply they can be answered independently when in practice the
  // agent is waiting on exactly one at a time.
  const pending = useMemo(
    () => projectSessionPendingInteractions(sessionDoc),
    [sessionDoc],
  )
  const question = useMemo(() => {
    const next = pending.find((i) => i.kind === 'question')
    if (!next) return null
    const body = parseQuestionBody(next.bodyJson)
    return body.questions.length > 0
      ? { interactionId: next.interactionId, body }
      : null
  }, [pending])

  const onAnswer = useCallback(
    async (answers: Record<string, InteractionAnswerValue>) => {
      if (!question) return
      await answerInteraction(
        boot.repo,
        sessionId,
        question.interactionId,
        answers,
        { projectId },
      )
    },
    [boot.repo, projectId, question, sessionId],
  )

  const onDismissQuestion = useCallback(async () => {
    if (!question) return
    await dismissInteraction(boot.repo, sessionId, question.interactionId, {
      projectId,
    })
  }, [boot.repo, projectId, question, sessionId])

  // Tasks are project state, so the session chip is present without an app.
  const [projectDoc] = useDocument(projectId as AnyDocumentId | undefined)
  const sourceCatalog = projectSources(projectDoc)
  const sessionCode = projectSessionCode(sessionDoc)
  const sourceId =
    (sessionDoc as { codeSourceId?: string } | undefined)?.codeSourceId ??
    sessionCode?.sourceId ??
    sourceCatalog.defaultSourceId
  const source = sourceCatalog.sources.find((item) => item.id === sourceId)
  // A file opens only through a source the project still lists: a session
  // whose recorded source was removed shows its files but cannot open them.
  const fileSourceId = source?.id
  const [vcsDoc] = useDocument(source?.vcsDocId as AnyDocumentId | undefined)
  const docBase = projectCode(vcsDoc)?.trunk

  // RFC 0307: a voice call on this session, started from the empty composer.
  const voice = useSessionVoice({
    repo: boot.repo,
    sessionId,
    ...(projectId ? { projectId } : {}),
    sessionTitle: String(
      (sessionDoc as { title?: unknown } | undefined)?.title ?? 'this session',
    ),
  })

  /**
   * The session header (RFC 0278, RFC 0284 §4a): a slot along the top of THIS
   * conversation, filled by whatever declares it.
   *
   * The document changes widget is built in, so it draws in a project with
   * nothing installed. Review adds file changes by declaration, and another
   * app can add its own widget here the way it adds one to the
   * sidebar: by declaring a fill against the slot, which is what
   * `fillSlotAll` picks up. Gating the whole surface on the Review app meant a
   * fresh project saw nothing and had no way to learn why.
   *
   * `useInstalledApps`, because chrome is installed in the WORLD (RFC 0295),
   * and a project-only read would hide an
   * installed widget in every project but the one it was installed into.
   *
   * A SILENT LAYOUT MEANS EVERYTHING, which is `fillSlotAll` rather than
   * `fillSlot`: this slot is a list, so installing an app that adds a widget is
   * the whole gesture and must not also require arranging a layout. The pane
   * has no layout in context, hence `undefined` — naming a subset is a
   * shell-level choice and lands with the geometry that would make it
   * meaningful.
   */
  const installed = useInstalledApps(projectDoc)
  const fileChanges = useMemo(
    () => sessionChangesContribution(installed),
    [installed],
  )
  const slotApps = useMemo(
    () =>
      installed.map((a) => ({
        id: a.id,
        name: a.name,
        ...(a.realm ? { realm: a.realm } : {}),
        ...(a.viewDocId ? { viewDocId: a.viewDocId } : {}),
        ...(a.panels ? { fills: a.panels } : {}),
      })),
    [installed],
  )
  // RFC 0313 3h: View more puts this session's Changes where the thread was.
  const [showChanges, setShowChanges] = useState(false)
  const sessionHeader = useMemo(
    () => fillSlotAll('sessionHeader', undefined, slotApps),
    [slotApps],
  )

  /**
   * The fold every widget in that slot is handed.
   *
   * Computed ONCE here rather than per widget: it is a pass over the complete
   * projected log, which is already in memory before the DOM window is cut.
   * Mounting stays bounded while the changed count remains exact.
   */
  const changes = useMemo(() => {
    // A paged session (RFC 0360): what earlier pages changed rides in the
    // page seed, and the live page's own completed turns add the rest.
    let seed: SessionChanges | null = null
    try {
      seed = pageSeedOf(decodeLogDoc(sessionDoc as never))?.changes ?? null
    } catch {
      seed = null
    }
    return mergeSessionChanges(
      seed,
      projectSessionChanges(completedTurnMessages(liveTranscript)),
    )
  }, [sessionDoc, liveTranscript])
  const visibleChanges = useMemo(
    () =>
      fileChanges
        ? changes
        : { files: [], edits: [], documents: changes.documents },
    [changes, fileChanges],
  )
  // RFC 0345 3c: in a session he shared, the owner says which documents the
  // agent wrote reach its members. A guest's window draws nothing here.
  const approvals = useDocumentApprovals({
    sessionId,
    projectDoc,
    transcript,
  })
  // RFC 0345 3f: a guest's message waits for the owner's Reply. The owner
  // sees Reply / Always reply / Ignore; the guest sees Ask the owner's agent.
  // A reply names this person's own model choice, never one off the log.
  const guestReplies = useGuestReplies(sessionId, {
    model: preferredComposerModel(),
  })
  // And in a guest's window, what he shared opens from the thread's cards.
  useGuestSessionDocuments(sessionId, sessionDoc as SessionDoc | undefined)
  // Only `mod init` writes a VcsDoc, so a desktop folder project has no trunk
  // on the log. The daemon's git server detects one per checkout; ask it, for
  // a branch this session wrote on. No answer means no git, and no Review action.
  const writtenBranch =
    sessionCode?.branch ??
    changes.files.find((f) => f.writtenOn?.branch)?.writtenOn?.branch
  const [serverBase, setServerBase] = useState<string>()
  // Keyed on whether Review offers the action, not on the contribution object,
  // which is rebuilt on every project or World change and would re-ask git.
  const reviewAction = fileChanges?.action
  useEffect(() => {
    // A base read for one branch or source is no answer for another.
    setServerBase(undefined)
    if (!reviewAction || docBase || !projectId || !writtenBranch) return
    let live = true
    fetchVcsFromServer('status', [writtenBranch], projectId, sourceId)
      .then((status) => {
        const base = (status as { base?: unknown } | null)?.base
        if (live && typeof base === 'string' && base) setServerBase(base)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [reviewAction, docBase, projectId, sourceId, writtenBranch])
  const prBase = docBase ?? serverBase
  const gitSources = sourceCatalog.sources.filter(
    (item) => item.kind === 'git',
  ).length
  // Titles for the document rows. The same catalog the sidebar reads; a
  // document this window cannot see keeps whatever name its call gave it.
  const catalog = useMemo(() => projectDocuments(projectDoc), [projectDoc])
  /**
   * The CURRENT name of anything a thread card points at.
   *
   * Off the registry the project document already carries, so it is the same
   * zero-hydration read the session list does and costs a card nothing. A
   * document renamed after the turn that wrote it then reads by its name now,
   * rather than by the title in a message from three days ago.
   *
   * Unknown ids return undefined rather than a placeholder: a session in
   * ANOTHER project is not in this registry, and the card falls back to the
   * title the call was made with, which is the last true thing recorded about
   * it. Inventing "(untitled)" there would replace a real name with a worse one.
   */
  const resolveTitle = useMemo(() => {
    const documents = new Map(
      projectDocuments(projectDoc).map((d) => [d.id, d.title]),
    )
    const sessions = new Map(
      projectSessions(projectDoc).map((s) => [s.id, s.title]),
    )
    return (target: CardTarget, id: string): string | undefined =>
      target === 'session' ? sessions.get(id) : documents.get(id)
  }, [projectDoc])

  /**
   * A comment's writer and words (RFC 0324 3d). The event names the comment
   * and carries its first line; the project's index says who wrote it, when,
   * and whose comment the agent's reply answered (RFC 0339 3d). The index has
   * no body, so a comment that has moved shows the event's line.
   */
  // RFC 0345 3g: a protected project keeps no index; read this session's
  // own comments and those of what it holds instead.
  const indexless = usesPrivateSessionCatalog(boot.repo)
  const lookupSubjects = useMemo(
    () =>
      indexless
        ? [
            sessionId,
            ...projectSessionHoldings(sessionDoc).map((h) => h.documentId),
          ]
        : [],
    [indexless, sessionId, sessionDoc],
  )
  const subjectComments = useCommentMap(projectId, lookupSubjects)
  const commentLookup = useMemo<CommentLookup>(() => {
    const listed = indexless ? subjectComments : commentListView(projectDoc)
    const byId = new Map(projectComments(listed).map((c) => [c.id, c]))
    const sessionIds = new Set(projectSessions(projectDoc).map((s) => s.id))
    return (commentId, author) => {
      const c = commentId ? byId.get(commentId) : undefined
      if (c) {
        return {
          author: sessionFace(projectAuthor(projectDoc, c)),
          ...(c.body ? { body: c.body } : {}),
          createdAt: c.createdAt,
          ...(c.target.documentId
            ? { documentId: String(c.target.documentId) }
            : {}),
        }
      }
      if (!author) return undefined
      // Not synced yet: the writer alone, a session by its title.
      return {
        author: sessionFace(
          projectAuthor(
            projectDoc,
            sessionIds.has(author)
              ? { author, authorSessionId: author }
              : { author },
          ),
        ),
      }
    }
  }, [projectDoc, indexless, subjectComments])

  /**
   * What the subthread under a post is doing (RFC 0302 S5).
   *
   * The SAME registry read as the title above, and deliberately so: a post that
   * fanned out six branches draws six cards, and opening six session documents
   * to light them is the over-fetch the registry status field exists to prevent
   * (RFC 0284 S8a). A session the registry cannot speak for gets no state at
   * all rather than a guessed one.
   */
  // RFC 0348 3e: on the World agent's thread, what it decided about each stop.
  const stopTrailing = useStopTrailing(sessionDoc)

  const resolveSessionState = useMemo(() => {
    const now = Date.now()
    const meta =
      (
        projectDoc as
          | {
              sessionMeta?: Record<
                string,
                { status?: unknown; statusAt?: unknown; completedAt?: number }
              >
            }
          | undefined
      )?.sessionMeta ?? {}
    return (sessionId: string) => {
      const entry = meta[sessionId]
      if (!entry) return undefined
      const status = registryStatus(entry, now)
      return subthreadState({
        ...(status ? { status } : {}),
        ...(entry.completedAt != null
          ? { completedAt: entry.completedAt }
          : {}),
      })
    }
  }, [projectDoc])

  // The World's and the project's skills, for the composer's `/` menu (RFC
  // 0334 3g): the ones a person writes and chooses. Read here rather than in
  // the composer, which has no repo in context in a slot or a test.
  const { skills: allSkills } = useSkills(projectDoc)
  const skills = useMemo(() => pickableSkills(allSkills), [allSkills])
  const worldId = readWorldPointer()

  /**
   * Write a skill named in the `/` menu, then open it (RFC 0283 S5).
   *
   * The description is a PLACEHOLDER the person is expected to replace: it is
   * the line every agent chooses the skill on, so it cannot be blank, and only
   * they know what the skill is for. Opening the document immediately is what
   * makes that obvious rather than a field they never see.
   */
  const createSkillFromComposer = useMemo(() => {
    if (!worldId || !projectId || !onOpenDocument) return undefined
    return (title: string) => {
      void (async () => {
        try {
          const skill = await createSkill(boot.repo as never, {
            worldId,
            projectId: projectId,
            title,
            description: `when to use ${title}`,
            instructions: `# ${title}\n\n`,
          })
          onOpenDocument(skill.documentId)
        } catch {
          // Offline or an unreachable World: the composer simply does not open
          // a document, and the message the person was writing is untouched.
        }
      })()
    }
  }, [boot.repo, worldId, projectId, onOpenDocument])

  /**
   * Write a dropped/pasted/picked file to its own document (RFC 0288).
   *
   * Called before send, not after: the codec treats an entry's array fields as
   * write-once at append, so a message's `attachments` must be complete when it
   * is appended.
   */
  const attach = useCallback(
    async (file: File): Promise<AttachmentRef> => {
      const source = await fileToSource(file)
      return attachFile(source, {
        processImage: browserProcessImage,
        repo: {
          create(doc) {
            return boot.repo.create(doc).documentId
          },
        },
      })
    },
    [boot.repo],
  )

  const send = useCallback(
    async (
      text: string,
      requestedModel: { provider: string; model: string },
      skills?: string[],
      attachments?: AttachmentRef[],
    ) => {
      // Native session → enqueue a run (with the chosen model) for the managed
      // inference worker to drain; captured session → append only (external
      // responder). `enqueueRun` branches on that, under here. Rejects so the
      // composer restores the draft.
      //
      // `sendToSession` rather than `enqueueRun` here: the composer strip
      // (RFC 0284 S8d) sends to a session it is not showing, and this was the
      // third place that wrote out the agent-derivation rule by hand.
      //
      // A question is open: what was typed is the answer, as "Other", not a
      // new message queued behind the question the agent is waiting on.
      // Rejects so the composer keeps the draft for when access arrives.
      if (waitingForAccess) throw new Error(`${guestOf} has not let you in yet`)
      const typed =
        question && !attachments?.length
          ? textAnswer(question.body, text)
          : null
      if (question && typed) {
        await answerInteraction(
          boot.repo,
          sessionId,
          question.interactionId,
          typed,
          { projectId },
        )
        return
      }
      const captured = await captureMessageView?.()
      await sendToSession(boot.repo, sessionId, projectId, {
        text,
        requestedModel,
        skills,
        attachments,
        ...(captured?.viewAttachments.length
          ? { viewAttachments: captured.viewAttachments }
          : {}),
        parents: frontier.take(visibleHeadsRef.current),
        // RFC 0297 §4e: stamp what the person is pointing at onto the message
        // that used it. Read through a ref rather than as a dependency, so a
        // selection made while the draft is being typed still lands and the
        // composer's send callback does not change identity on every click.
        ...(selectionRef.current ? { selection: selectionRef.current } : {}),
        ...((captured?.view ?? messageView)
          ? { view: captured?.view ?? messageView }
          : {}),
      })
    },
    [
      boot.repo,
      sessionId,
      projectId,
      frontier,
      messageView,
      captureMessageView,
      question,
      waitingForAccess,
      guestOf,
    ],
  )

  /**
   * The session header's widgets. A widget with nothing to say renders null, so
   * an empty header costs no pixels — the same contract the header and composer
   * slots hold.
   */
  const headerWidgets = (
    <>
      {/* RFC 0277 S14: where this session works, before what it changed. The
          one widget that shrinks, so a narrow pane keeps the rest whole. */}
      <div className="min-w-0 shrink">
        <SessionWhere
          sessionId={sessionId}
          projectId={projectId}
          // S14: the project lives here with the device and folder. Moving
          // it is offered only while nothing has been said.
          {...(projects ? { projects } : {})}
          {...(onSelectProject && transcript.length === 0 && !hasMore
            ? { onSelectProject }
            : {})}
        />
      </div>
      {/* RFC 0345 3i: Share, when sharing is on and the session is ours. On a
          Mac the window has no sharing repo; its daemon shares (3h). */}
      {(boot.sharing || desktopInviteFor()) && (
        <div className="shrink-0">
          <SessionShare sessionId={sessionId} />
        </div>
      )}
      {sessionHeader.map((fill) =>
        fill.kind === 'builtin' ? (
          <div key={fill.builtin.id} className="shrink-0">
            {fill.builtin.render({
              sessionId,
              ...(projectId ? { projectId } : {}),
              changes: visibleChanges,
              ...(fileSourceId ? { fileSourceId } : {}),
              // The fold reads the whole projected log, not the mounted
              // window, so a cut DOM window does not make the count partial.
              partial: false,
              documents: catalog,
              ...(onOpenDocument ? { onOpenDocument } : {}),
              ...(projectId ? { onViewMore: () => setShowChanges(true) } : {}),
              // With several Git sources, offer only the branch this session
              // wrote on: its recorded code ref, else where its writes landed.
              ...(fileChanges?.action === 'reviewBranch' &&
              prBase &&
              projectId &&
              projectDoc &&
              onOpenDocument &&
              (gitSources <= 1 || writtenBranch)
                ? {
                    reviewBase: prBase,
                    ...(gitSources > 1 ? { reviewBranch: writtenBranch } : {}),
                    onReviewBranch: (branch: string) => {
                      void openBranchReview(
                        boot.repo as never,
                        projectId,
                        projectDoc,
                        installed,
                        sourceId,
                        branch,
                        prBase,
                      )
                        .then(onOpenDocument)
                        .catch((e) =>
                          console.error('[review] could not open', e),
                        )
                    },
                  }
                : {}),
              ...(projectDoc && projectId
                ? {
                    tasks: {
                      repo: boot.repo as never,
                      projectId,
                      projectDoc,
                    },
                  }
                : {}),
            })}
          </div>
        ) : (
          <div key={fill.app.id} className="shrink-0">
            <InstalledSlot
              app={fill.app}
              slot="sessionHeader"
              projectId={projectId}
              sessionId={sessionId}
            />
          </div>
        ),
      )}
    </>
  )

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
      {/* Into the pane's header when it published one, in place when it did
          not. The widgets are the same elements either way, so nothing about
          them has to know which host it got. */}
      {headerSlot ? createPortal(headerWidgets, headerSlot) : headerWidgets}
      <div className="min-h-0 flex-1">
        {showChanges && projectId ? (
          <SessionChangesPanel
            repo={boot.repo as never}
            projectId={projectId}
            sessionId={sessionId}
            changes={visibleChanges}
            includeFiles={!!fileChanges}
            documents={catalog}
            onBack={() => setShowChanges(false)}
            {...(onOpenDocument ? { onOpenDocument } : {})}
          />
        ) : (
          <Thread
            transcript={transcript}
            {...(waitingForAccess
              ? {
                  emptyText: `Waiting for ${guestOf} to let you in. This needs their Mod open.`,
                }
              : unreachable
                ? {
                    emptyText:
                      'This session has not opened here. It may not be shared with you, or it has not reached this device yet.',
                  }
                : {})}
            projectId={projectId}
            {...(fileSourceId ? { sourceId: fileSourceId } : {})}
            fileChanges={fileChanges}
            resolveTitle={resolveTitle}
            resolveSessionState={resolveSessionState}
            commentLookup={commentLookup}
            stopTrailing={stopTrailing}
            {...(projectId
              ? {
                  // RFC 0301 S9: the undo on an agent's "edited" card.
                  renderUndo: (documentId: string, hash: string) => (
                    <EditUndo
                      repo={boot.repo}
                      projectId={projectId}
                      documentId={documentId}
                      hash={hash}
                    />
                  ),
                }
              : {})}
            {...(onOpenDocument ? { onOpenDocument } : {})}
            {...(onOpenSession ? { onOpenSession } : {})}
            header={
              <>
                {/* RFC 0302 S7: a subthread says where it came from, at the top
                  of its own conversation, so a pane opened from a card is not
                  a thread that begins mid-sentence. It is a line, not a link:
                  the way back is Back, in the header, in one place. */}
                {origin ? (
                  <div className="pb-2 text-[0.72rem] text-muted-foreground italic">
                    {`From ${
                      resolveTitle('session', origin.sourceSessionId) ??
                      'another conversation'
                    }`}
                  </div>
                ) : null}
                {hasMore ? (
                  <div className="flex justify-center pb-2">
                    <button
                      type="button"
                      onClick={loadMore}
                      className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs hover:text-foreground"
                    >
                      Load earlier messages
                    </button>
                  </div>
                ) : null}
              </>
            }
          />
        )}
      </div>
      <DocumentApprovals
        pending={approvals.pending}
        onChoose={(documentId, choice) =>
          void approvals.choose(documentId, choice)
        }
        error={approvals.error}
        {...(onOpenDocument ? { onOpenDocument } : {})}
      />
      <ProjectAccessApproval sessionId={sessionId} />
      <GuestReplies
        waiting={guestReplies.waiting}
        guest={guestReplies.guest}
        onGuest={(choice) => void guestReplies.chooseGuest(choice)}
        canAddAgent={guestReplies.canAddAgent}
        error={guestReplies.error}
      />
      {question ? (
        <QuestionForm
          // Remount on a new question so a half-filled form never carries
          // selections from the previous one.
          key={question.interactionId}
          body={question.body}
          onSubmit={onAnswer}
          onDismiss={onDismissQuestion}
        />
      ) : null}
      {unreachable ? null : (
        <Composer
          sessionId={sessionId}
          onSend={send}
          onDraftChange={onDraftChange}
          // RFC 0302 S6: in a subthread, say so. The reply stays here; the
          // conversation it came out of is one Back away and is NOT where this
          // goes.
          {...(origin
            ? {
                target: `Replying in ${String(
                  (sessionDoc as { title?: unknown } | undefined)?.title ??
                    'this subthread',
                )}`,
              }
            : {})}
          onAttach={attach}
          {...(boot.sharing
            ? { projectAttachment: <SessionProjects sessionId={sessionId} /> }
            : {})}
          voice={voice}
          waiting={waiting}
          reading={reading}
          busy={busy}
          onStop={stop}
          devices={devices}
          skills={skills}
          onCreateSkill={createSkillFromComposer}
          // Nobody has spoken yet, so nothing is in flight to be resolved against
          // a registry that is about to change. Read off the transcript rather
          // than a stored flag: the log already knows, and a second copy of that
          // fact can disagree with it. `hasMore` matters because a windowed
          // transcript can be empty while earlier pages exist — a long session
          // scrolled to its top is not an unstarted one.
          unstarted={transcript.length === 0 && !hasMore}
          // RFC 0297 S5: what this session has in hand, directly above the box
          // you are about to type in, wherever that box is drawn. It draws
          // nothing when nothing is held.
          holdings={
            <SessionHoldingsBar
              sessionId={sessionId}
              {...(projectId ? { projectId } : {})}
              {...(onOpenDocument ? { onOpenDocument } : {})}
              framed={false}
            />
          }
        />
      )}
    </div>
  )
}
